import { ActivityEntityName, DateUtility, isPayablePaymentType, Payment } from '@jms/model';

/** What one Payment row prints, beside its badge and status pill. */
export interface PaymentCardRow {
  /** Headline. */
  readonly amount: number;
  /** "7 Oct 2026 · EFT · 1234567": when, how, which reference. */
  readonly meta: string;
  /** The staff member who recorded it, under the legacy column's name for this side of the ledger. */
  readonly receiver?: { readonly label: string; readonly name: string };
  /** Where it was recorded (WorkBuddy, Stripe, an accounting sync): the legacy Source column. */
  readonly source?: string;
}

/**
 * Absent parts drop out of the meta line instead of leaving empty separators. The date reads in the
 * payment's own timezone: it was captured as a calendar day there, and another zone can shift it a day.
 * Without one it reads in the browser's zone, as the legacy payments grid did, not DateUtility's UTC default.
 *
 * `receiver` is the user who recorded the payment, labelled as legacy did: Received By on an invoice, Paid By
 * on a bill. `payer` is never shown: no legacy payments view printed it.
 */
export function paymentCardRow(payment: Partial<Payment>): PaymentCardRow {

  const date = payment.date ? DateUtility.formatDateTime(payment.date, payment.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, 'd MMM yyyy') : null;
  const meta = [date, payment.method?.name, payment.reference]
    .map(part => part?.trim())
    .filter((part): part is string => !!part)
    .join(' · ');
  const receiver = payment.receiver?.name?.trim();
  return {
    amount: payment.amount ?? 0,
    meta: meta || 'Payment',
    ...(receiver ? { receiver: { label: isPayablePaymentType(payment.type) ? 'Paid by' : 'Received by', name: receiver } } : {}),
    ...(payment.source ? { source: payment.source } : {}),
  };
}

/**
 * Whether a payment card opens its preview. A credit allocation is listed as a payment but has no payment record,
 * and with the Payments screens off the preview route refuses it, so a clickable card would do nothing.
 */
export function paymentCardOpenable(payment: Partial<Payment> | null | undefined, canAccessPayments: boolean, paymentScreensEnabled: boolean): boolean {

  return canAccessPayments && paymentScreensEnabled && !!payment?._id && payment.data !== ActivityEntityName.CreditAllocation;
}
